import { useEffect, useRef, useState } from 'react';
import type { PhotoPose } from '@st/contract';
import { imageContentTypes, isAllowedImageType, maxImageBytes } from '@st/contract';
import { useUploadProgressPhoto, type PhotoUploadStage } from '../../api/queries';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { cx } from '../../lib/cx';
import { formatNumber, todayIso } from '../../lib/format';
import { PHOTO_POSE_KEY } from '../../lib/photos';
import {
  Button,
  Field,
  Input,
  Modal,
  ProgressBar,
  Select,
  Textarea,
  borderClass,
  controlClass,
} from '../../ui';

const POSES: readonly PhotoPose[] = ['front', 'side', 'back', 'other'];

/** Redoslijed koraka; iz njega se crta traka napretka i bira poruka. */
const STAGES: readonly PhotoUploadStage[] = ['grant', 'upload', 'confirm'];

const STAGE_KEY: Record<PhotoUploadStage, string> = {
  grant: 'progress.stageGrant',
  upload: 'progress.stageUpload',
  confirm: 'progress.stageConfirm',
};

export interface ProgressPhotoModalProps {
  open: boolean;
  onClose: () => void;
  /** Javlja se tek kad zapis stvarno postoji na serveru. */
  onUploaded: () => void;
}

/**
 * Prijenos fotografije napretka.
 *
 * Tok je namjerno u tri koraka (dozvola → bajtovi u R2 → potvrda), jer zapis u
 * bazi nastaje TEK pri potvrdi — prekinut prijenos zato ne ostavlja redak s
 * URL-om koji vodi u 404. Ekran pamti ključ već prenesene datoteke, pa ponovni
 * pokušaj nakon pale potvrde ne šalje istu sliku drugi put.
 *
 * Tip i veličina se provjeravaju PRIJE slanja: bez toga korisnik čeka prijenos
 * pa dobije 413, s odabranom slikom koju mora tražiti iznova.
 *
 * PRIVATNOST: ovaj ekran zove isključivo `/v1/progress/photos` — vlastite
 * fotografije prijavljenog klijenta. Trener ih vidi samo ako klijent odobri
 * pristup (`coaching_relationships.photo_access_granted_at`), i to kroz zasebnu
 * rutu koju portal odavde ne dira.
 */
export function ProgressPhotoModal({ open, onClose, onUploaded }: ProgressPhotoModalProps) {
  const { t, locale } = useTranslation();
  const upload = useUploadProgressPhoto();

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const [takenOn, setTakenOn] = useState(todayIso());
  const [pose, setPose] = useState<PhotoPose>('front');
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<PhotoUploadStage | null>(null);
  const [failure, setFailure] = useState<{ title: string; help: string } | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  /** Ključ datoteke koja je već legla u R2 — ponovni pokušaj kreće od potvrde. */
  const uploadedKey = useRef<string | null>(null);

  // Objekt-URL pregleda se oslobađa; inače bi svaka odabrana slika ostala u memoriji.
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function reset(): void {
    setFile(null);
    setFileError(undefined);
    setTakenOn(todayIso());
    setPose('front');
    setNote('');
    setStage(null);
    setFailure(null);
    uploadedKey.current = null;
  }

  function close(): void {
    if (upload.isPending) return;
    reset();
    onClose();
  }

  function pick(chosen: File | null): void {
    // Nova datoteka poništava ključ prethodne — inače bi se potvrdila stara slika.
    uploadedKey.current = null;
    setFailure(null);
    setFile(chosen);

    if (!chosen) {
      setFileError(undefined);
      return;
    }
    if (!isAllowedImageType(chosen.type)) {
      setFileError(t('progress.photoTypeInvalid'));
      return;
    }
    if (chosen.size > maxImageBytes) {
      setFileError(
        t('progress.photoTooLarge', { max: formatNumber(maxImageBytes / (1024 * 1024), locale) }),
      );
      return;
    }
    setFileError(undefined);
  }

  async function submit(): Promise<void> {
    if (!file) {
      setFileError(t('progress.photoRequired'));
      return;
    }
    if (fileError) return;

    setFailure(null);
    try {
      await upload.mutateAsync({
        file,
        takenOn,
        pose,
        ...(note.trim() ? { note: note.trim() } : {}),
        uploadedKey: uploadedKey.current,
        onStage: setStage,
        onUploaded: (key) => {
          uploadedKey.current = key;
        },
      });
      reset();
      onUploaded();
    } catch (err) {
      const msg = toErrorMessage(t, err);
      setFailure({ title: msg.title, help: msg.help });
    } finally {
      setStage(null);
    }
  }

  const stageIndex = stage ? STAGES.indexOf(stage) + 1 : 0;
  const canSubmit = Boolean(file) && !fileError;

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('progress.addPhoto')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={upload.isPending}>
            {t('common.cancel')}
          </Button>
          <Button loading={upload.isPending} disabled={!canSubmit} onClick={() => void submit()}>
            {failure ? t('common.retry') : t('progress.uploadPhoto')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Zašto je sigurno poslati sliku — pitanje koje se postavlja prije prijenosa, ne poslije. */}
        <p className="rounded-md bg-bg-alt px-3 py-2 text-[13px] leading-relaxed text-ink-muted">
          {t('progress.photoPrivacy')}
        </p>

        {failure && (
          <div role="alert" className="flex flex-col gap-1 text-[13px] text-danger">
            <span>{failure.title}</span>
            <span className="text-ink-muted">
              {uploadedKey.current ? t('progress.retryFromConfirm') : failure.help}
            </span>
          </div>
        )}

        <Field
          id="photo-file"
          label={t('progress.photoFile')}
          hint={t('progress.photoLimits', {
            max: formatNumber(maxImageBytes / (1024 * 1024), locale),
          })}
          error={fileError}
          required
        >
          <input
            id="photo-file"
            type="file"
            accept={imageContentTypes.join(',')}
            required
            aria-invalid={fileError ? true : undefined}
            aria-describedby={fileError ? 'photo-file-error' : 'photo-file-hint'}
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
            className={cx(
              controlClass,
              borderClass(!!fileError),
              'file:mr-3 file:rounded-pill file:border-0 file:bg-bg-alt file:px-4 file:py-1.5',
              'file:font-body file:text-[13px] file:text-ink',
            )}
          />
        </Field>

        {preview && !fileError && (
          <img
            src={preview}
            alt={t('progress.photoPreview')}
            className="h-40 w-32 rounded-md object-cover"
          />
        )}

        <Input
          id="photo-taken-on"
          type="date"
          label={t('progress.takenOn')}
          value={takenOn}
          max={todayIso()}
          onChange={(e) => setTakenOn(e.target.value)}
        />

        <Select
          id="photo-pose"
          label={t('progress.pose')}
          value={pose}
          onChange={(e) => setPose(e.target.value as PhotoPose)}
          options={POSES.map((p) => ({ value: p, label: t(PHOTO_POSE_KEY[p]) }))}
        />

        <Textarea
          id="photo-note"
          label={t('progress.photoNote')}
          rows={2}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {/*
          Prijenos zna trajati; bez ovoga korisnik gleda gumb koji se vrti i ne
          zna je li datoteka uopće krenula. Koraci su stvarni, ne dekorativni.
        */}
        {upload.isPending && stage && (
          <ProgressBar
            value={stageIndex}
            max={STAGES.length}
            label={t(STAGE_KEY[stage])}
            valueLabel={`${stageIndex}/${STAGES.length}`}
          />
        )}
        <span aria-live="polite" className="sr-only">
          {upload.isPending && stage ? t(STAGE_KEY[stage]) : ''}
        </span>
      </div>
    </Modal>
  );
}
